import type * as Theme from '#theme'
import type { Image } from '../types/media'
import { resolveThemeAssetImage } from '../theme/utils/resolveThemeAsset'
import { themeAssetUrls } from '#theme-assets'
import { computed, useCoreConfig, useTheme } from '#imports'
import type { ComputedRef } from '#imports'

/**
 * Resolves a brand logo for the given slot.
 *
 * Resolution: `site.logo[slot]` (consumer override in nuxt.config.ts)
 * falls back to `theme.logo[slot]` (brand default shipped with the theme).
 *
 * Theme-shipped logos are declared with brand-relative keys
 * (e.g. `spilnu/logo/primary.png`) and resolved to hashed URLs via the
 * Vite-generated `#theme-assets` map. Site overrides that already look
 * like URLs (absolute, `http(s)://`, `data:`) pass through unchanged.
 *
 * @example
 * const logo = useThemeLogo('icon')
 * // -> ComputedRef<Image | undefined>
 * // <img :src="logo?.src" :width="logo?.width" :height="logo?.height">
 */
export function useThemeLogo(slot: Theme.LogoSlot): ComputedRef<Image | undefined> {
    const config = useCoreConfig()
    const { theme } = useTheme()

    return computed(() => {
        const raw = config.site.logo?.[slot] ?? theme.value.logo?.[slot]
        return resolveThemeAssetImage(raw, themeAssetUrls)
    })
}
